import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragOverlay
} from '@dnd-kit/core'
import { 
  arrayMove, 
  SortableContext, 
  sortableKeyboardCoordinates, 
  verticalListSortingStrategy 
} from '@dnd-kit/sortable'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useExercisePlanStore } from '@/stores/exercisePlanStore'
import {
  Plus,
  Edit,
  Save,
  Clock,
  Target,
  Users,
  Settings,
  GripVertical
} from 'lucide-react'

// Import section components
import PlanSection from './PlanSection'
import ExerciseCard from './ExerciseCard'

const PlanBuilder = ({ plan, onOpenExerciseLibrary }) => {
  const [isEditingPlan, setIsEditingPlan] = useState(false)
  const [planName, setPlanName] = useState(plan.name)
  const [planDescription, setPlanDescription] = useState(plan.description)
  const [planDuration, setPlanDuration] = useState(plan.duration)
  const [planDifficulty, setPlanDifficulty] = useState(plan.difficulty)
  const [activeId, setActiveId] = useState(null)
  const [draggedExercise, setDraggedExercise] = useState(null)

  const {
    updatePlan,
    addSection,
    updateSection,
    deleteSection,
    addExerciseToSection,
    updateExercise,
    deleteExercise,
    reorderExercises
  } = useExercisePlanStore()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleSavePlanDetails = () => {
    updatePlan(plan.id, {
      name: planName,
      description: planDescription,
      duration: parseInt(planDuration),
      difficulty: planDifficulty
    })
    setIsEditingPlan(false)
  }

  const handleCancelEdit = () => {
    setPlanName(plan.name)
    setPlanDescription(plan.description)
    setPlanDuration(plan.duration)
    setPlanDifficulty(plan.difficulty)
    setIsEditingPlan(false)
  }

  const handleAddSection = () => {
    const sectionName = prompt('Enter section name:')
    if (sectionName) {
      addSection(plan.id, {
        name: sectionName,
        type: 'custom'
      })
    }
  }

  const getTotalExercises = () => {
    return plan.sections.reduce((total, section) => total + section.exercises.length, 0)
  }

  const getEstimatedTime = () => {
    let totalTime = 0
    plan.sections.forEach(section => {
      section.exercises.forEach(exercise => {
        // Estimate time based on sets, reps, and rest
        const workTime = exercise.sets * 30 // Assume 30 seconds per set
        const restTime = exercise.sets * (exercise.rest || 60)
        totalTime += workTime + restTime
      })
    })
    return Math.round(totalTime / 60) // Convert to minutes
  }

  const handleDragStart = (event) => {
    const { active } = event
    setActiveId(active.id)
    
    // Find the dragged exercise
    for (const section of plan.sections) {
      const exercise = section.exercises.find(ex => ex.id === active.id)
      if (exercise) {
        setDraggedExercise(exercise)
        break
      }
    }
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    setActiveId(null)
    setDraggedExercise(null)

    if (!over) return

    // Find source and destination sections
    let sourceSection = null
    let destSection = null
    let sourceIndex = -1
    let destIndex = -1

    // Find source section and index
    for (const section of plan.sections) {
      const index = section.exercises.findIndex(ex => ex.id === active.id)
      if (index !== -1) {
        sourceSection = section
        sourceIndex = index
        break
      }
    }

    // Find destination section and index
    if (over.id.startsWith('section-')) {
      // Dropped on a section
      const sectionId = over.id.replace('section-', '')
      destSection = plan.sections.find(s => s.id === sectionId)
      destIndex = destSection ? destSection.exercises.length : 0
    } else {
      // Dropped on another exercise
      for (const section of plan.sections) {
        const index = section.exercises.findIndex(ex => ex.id === over.id)
        if (index !== -1) {
          destSection = section
          destIndex = index
          break
        }
      }
    }

    if (!sourceSection || !destSection) return

    if (sourceSection.id === destSection.id) {
      // Reordering within the same section
      if (sourceIndex !== destIndex) {
        const newExercises = arrayMove(sourceSection.exercises, sourceIndex, destIndex)
        reorderExercises(plan.id, sourceSection.id, newExercises)
      }
    } else {
      // Moving between sections
      const sourceExercise = sourceSection.exercises[sourceIndex]
      
      // Remove from source
      const newSourceExercises = sourceSection.exercises.filter((_, index) => index !== sourceIndex)
      reorderExercises(plan.id, sourceSection.id, newSourceExercises)
      
      // Add to destination
      const newDestExercises = [...destSection.exercises]
      newDestExercises.splice(destIndex, 0, sourceExercise)
      reorderExercises(plan.id, destSection.id, newDestExercises)
    }
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Plan Header */}
      <div className="bg-white border-b border-slate-200 p-6">
        {isEditingPlan ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Plan Name
                </label>
                <Input
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  placeholder="Enter plan name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Duration (minutes)
                </label>
                <Input
                  type="number"
                  value={planDuration}
                  onChange={(e) => setPlanDuration(e.target.value)}
                  placeholder="60"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description
              </label>
              <Textarea
                value={planDescription}
                onChange={(e) => setPlanDescription(e.target.value)}
                placeholder="Describe this workout plan"
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Difficulty Level
              </label>
              <select
                value={planDifficulty}
                onChange={(e) => setPlanDifficulty(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSavePlanDetails} variant="fitness">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button onClick={handleCancelEdit} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">{plan.name}</h2>
                <p className="text-slate-600 mt-1">{plan.description}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setIsEditingPlan(true)}
                size="sm"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Plan
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-center text-blue-600 mb-1">
                  <Clock className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Duration</span>
                </div>
                <div className="text-lg font-semibold text-blue-900">
                  {plan.duration} min
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-3">
                <div className="flex items-center text-green-600 mb-1">
                  <Target className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Exercises</span>
                </div>
                <div className="text-lg font-semibold text-green-900">
                  {getTotalExercises()}
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="flex items-center text-purple-600 mb-1">
                  <Users className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Assigned</span>
                </div>
                <div className="text-lg font-semibold text-purple-900">
                  {plan.assignedClients.length}
                </div>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-3">
                <div className="flex items-center text-orange-600 mb-1">
                  <Settings className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Difficulty</span>
                </div>
                <div className="text-lg font-semibold text-orange-900 capitalize">
                  {plan.difficulty}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plan Sections */}
      <div className="flex-1 overflow-y-auto p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="space-y-6">
            {plan.sections.map((section) => (
              <PlanSection
                key={section.id}
                section={section}
                planId={plan.id}
                onOpenExerciseLibrary={onOpenExerciseLibrary}
              />
            ))}
            
            {/* Add Section Button */}
            <Card className="border-2 border-dashed border-slate-300 hover:border-slate-400 transition-colors">
              <CardContent className="p-6">
                <Button
                  variant="ghost"
                  onClick={handleAddSection}
                  className="w-full h-16 text-slate-600 hover:text-slate-900"
                >
                  <Plus className="h-6 w-6 mr-2" />
                  Add Custom Section
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeId && draggedExercise ? (
              <div className="transform rotate-3 opacity-90">
                <ExerciseCard
                  exercise={draggedExercise}
                  planId={plan.id}
                  sectionId=""
                  isDragging={true}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}

export default PlanBuilder